import {
	PRODUCT_CHECK_SCHEDULE_PRODUCT_FILTER_BY,
	PRODUCT_EXPIRY_CHECK_SCHEDULE_TYPE,
	RESTOCK_TYPE,
} from '../../constants';
import {
	getProductExpiryScheduleItemAdaptiveDueDetails,
	getProductExpiryScheduleItemDueDetails,
	getProductExpiryScheduleRepeatInterval,
	toProductExpiryScheduleId,
} from './productExpiryScheduleDue';
import { getLocalTime } from '../time/time';

const toStringId = (value) => {
	if (!value) {
		return null;
	}

	if (typeof value === 'string') {
		return value;
	}

	if (value._id) {
		return value._id.toString();
	}

	if (value.id) {
		return value.id.toString();
	}

	if (typeof value.toString === 'function') {
		return value.toString();
	}

	return null;
};

const normalizePositiveInt = (value) => {
	const parsedValue = Number.parseInt(value, 10);
	return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : 0;
};

export const getProductExpiryScheduleSelectionSeed = ({
	machineId,
	plannedDate,
	timezone,
}) => {
	const machineIdString = toStringId(machineId) || 'no-machine';
	let plannedDay = 'no-date';

	if (plannedDate) {
		if (timezone) {
			const localPlannedDate = getLocalTime(timezone, plannedDate);
			if (localPlannedDate.isValid()) {
				plannedDay = localPlannedDate.format('YYYY-MM-DD');
			}
		}
		else {
			const parsedPlannedDate = new Date(plannedDate);
			if (!Number.isNaN(parsedPlannedDate.getTime())) {
				plannedDay = parsedPlannedDate.toISOString().slice(0, 10);
			}
		}
	}

	return `${machineIdString}:${plannedDay}`;
};

const hashString = (value = '') => {
	let hash = 7;
	for (let index = 0; index < value.length; index += 1) {
		hash = (31 * hash + value.charCodeAt(index)) % 2147483647;
	}

	return hash;
};

const checkFreshFoodOption = ({ scheduleFreshFoodOption, isFreshFood }) => {
	if (!scheduleFreshFoodOption || scheduleFreshFoodOption === RESTOCK_TYPE.ALL_PRODUCTS) {
		return true;
	}

	if (scheduleFreshFoodOption === RESTOCK_TYPE.FRESH_FOOD_ONLY) {
		return Boolean(isFreshFood);
	}

	if (scheduleFreshFoodOption === RESTOCK_TYPE.EXCLUDE_FRESH_FOOD) {
		return !isFreshFood;
	}

	return true;
};

// Normalize a schedule's product/tag filters into Sets once, so per-item
// matching does not re-convert the same ID arrays for every planogram item.
const normalizeScheduleForMatching = (schedule) => {
	const {
		filterProductsBy,
		freshFoodOption,
		applyToAllProducts,
		noProductExclusion,
		selectedProducts = [],
		excludedProducts = [],
		applyToAllProductTags,
		noProductTagExclusion,
		productTags = [],
		excludedProductTags = [],
	} = schedule;

	return {
		filterProductsBy,
		freshFoodOption,
		applyToAllProducts,
		noProductExclusion,
		applyToAllProductTags,
		noProductTagExclusion,
		selectedProductIds: new Set(selectedProducts.map(toStringId).filter(Boolean)),
		excludedProductIds: new Set(excludedProducts.map(toStringId).filter(Boolean)),
		selectedProductTagIds: new Set(productTags.map(toStringId).filter(Boolean)),
		excludedProductTagIds: new Set(excludedProductTags.map(toStringId).filter(Boolean)),
	};
};

// item must come from normalizedItems (string productId/productTagIds already applied)
const matchesSchedule = ({ normalizedSchedule, item }) => {
	if (!normalizedSchedule || !item) {
		return false;
	}

	const { productId } = item;
	if (!productId) {
		return false;
	}

	const productTagIds = item.productTagIds || [];
	const {
		filterProductsBy,
		freshFoodOption,
		applyToAllProducts,
		noProductExclusion,
		selectedProductIds,
		excludedProductIds,
		applyToAllProductTags,
		noProductTagExclusion,
		selectedProductTagIds,
		excludedProductTagIds,
	} = normalizedSchedule;

	if (!checkFreshFoodOption({
		scheduleFreshFoodOption: freshFoodOption,
		isFreshFood: item.isFreshFood,
	})) {
		return false;
	}

	if (filterProductsBy === PRODUCT_CHECK_SCHEDULE_PRODUCT_FILTER_BY.TAG) {
		if (applyToAllProductTags) {
			if (noProductTagExclusion) {
				return true;
			}

			// Match existing behavior: products without tags are included when only exclusions are defined.
			return !productTagIds.length || productTagIds.some(tagId => !excludedProductTagIds.has(tagId));
		}

		return productTagIds.some(tagId => selectedProductTagIds.has(tagId));
	}

	if (selectedProductIds.has(productId)) {
		return true;
	}

	if (applyToAllProducts) {
		if (noProductExclusion) {
			return true;
		}

		return !excludedProductIds.has(productId);
	}

	return false;
};

const getSortedLimitedItems = ({ items, maxCheckedProducts, seed }) => {
	if (!maxCheckedProducts || items.length <= maxCheckedProducts) {
		return items;
	}

	return [...items]
		.sort((left, right) => {
			const leftScore = hashString(`${seed}:${left.planogramItemId}`);
			const rightScore = hashString(`${seed}:${right.planogramItemId}`);

			if (leftScore === rightScore) {
				return left.planogramItemId.localeCompare(right.planogramItemId);
			}

			return leftScore - rightScore;
		})
		.slice(0, maxCheckedProducts);
};

const getScheduledPlanogramItemIds = ({
	items = [],
	productExpiryCheckSchedules = [],
	seed = '',
	plannedDate,
	expiryDateThresholdDays,
	averageDaysSinceLastService,
	planogramItemsVendRate,
	timezone,
}) => {
	const selectedPlanogramItemIds = new Set();
	const normalizedItems = items
		.map(item => ({
			planogramItemId: toStringId(item?.planogramItemId),
			productId: toStringId(item?.productId),
			productTagIds: (item?.productTagIds || []).map(toStringId).filter(Boolean),
			isFreshFood: Boolean(item?.isFreshFood),
			isMarket: Boolean(item?.isMarket),
			quantity: item?.quantity,
			presentProductQuantity: item?.presentProductQuantity,
			hasExpiryDates: Object.prototype.hasOwnProperty.call(item || {}, 'expiryDates'),
			expiryDates: Object.prototype.hasOwnProperty.call(item || {}, 'expiryDates') ? item.expiryDates : undefined,
			productExpiryScheduleLastCheckedAt: item?.productExpiryScheduleLastCheckedAt,
			lastProductChangeTime: item?.lastProductChangeTime,
			planogramItemVendRate: item?.planogramItemVendRate,
		}))
		.filter(item => item.planogramItemId && item.productId);

	productExpiryCheckSchedules.forEach((schedule, scheduleIndex) => {
		if (!schedule || !getProductExpiryScheduleRepeatInterval(schedule)) {
			return;
		}

		const normalizedSchedule = normalizeScheduleForMatching(schedule);
		const matchingItems = normalizedItems.filter(item => matchesSchedule({ normalizedSchedule, item }));
		if (!matchingItems.length) {
			return;
		}

		const maxCheckedProducts = normalizePositiveInt(schedule?.maxCheckedProducts);
		const scheduleId = toStringId(schedule?.id) || toStringId(schedule?._id) || `schedule-${scheduleIndex}`;
		const scheduleSeed = `${seed}:${scheduleId}`;
		let limitedItems;

		if (schedule.type === PRODUCT_EXPIRY_CHECK_SCHEDULE_TYPE.FLEXIBLE) {
			const dueItems = matchingItems.map(item => {
				const dueOptions = {
					schedule,
					item,
					scheduleId: toProductExpiryScheduleId(schedule) || scheduleId,
					plannedDate,
					expiryDateThresholdDays,
					averageDaysSinceLastService,
					planogramItemsVendRate,
					timezone,
				};
				const adaptiveDueDetails = getProductExpiryScheduleItemAdaptiveDueDetails(dueOptions);
				const dueDetails = adaptiveDueDetails.due
					? adaptiveDueDetails
					: getProductExpiryScheduleItemDueDetails(dueOptions);

				return {
					item,
					due: dueDetails.due,
					adaptive: adaptiveDueDetails.due,
				};
			}).filter(item => item.due);
			const adaptiveDueItems = dueItems
				.filter(item => item.adaptive)
				.map(item => item.item);
			const intervalDueItems = dueItems
				.filter(item => !item.adaptive)
				.map(item => item.item);
			// Urgent adaptive checks must not be hidden by the routine per-visit cap.
			const remainingLimit = maxCheckedProducts
				? Math.max(0, maxCheckedProducts - adaptiveDueItems.length)
				: 0;
			const limitedIntervalDueItems = maxCheckedProducts
				? (remainingLimit ? getSortedLimitedItems({
					items: intervalDueItems,
					maxCheckedProducts: remainingLimit,
					seed: scheduleSeed,
				}) : [])
				: intervalDueItems;
			limitedItems = [
				...adaptiveDueItems,
				...limitedIntervalDueItems,
			];
		}
		else {
			limitedItems = getSortedLimitedItems({
				items: matchingItems,
				maxCheckedProducts,
				seed: scheduleSeed,
			});
		}

		limitedItems.forEach(item => {
			selectedPlanogramItemIds.add(item.planogramItemId);
		});
	});

	return selectedPlanogramItemIds;
};

export default getScheduledPlanogramItemIds;
